import type { CrmPublicSite } from '@/lib/crm-public-site'
import type {
  CrmCustomBlock,
  CrmCustomMenu,
  CrmCustomMenuItem,
  CrmCustomSiteContactNotify,
  CrmCustomSiteFooter,
  CrmCustomSitePage,
  CrmCustomSiteProduct,
  CrmCustomSiteSettings,
  CrmCustomSiteTheme,
} from '@/types/crm'

/**
 * Custom-site public runtime helpers.
 *
 * Keep this module browser-only and dependency-light: it is loaded on every
 * custom official website, so it must not import the Supabase/admin editor
 * module (`crm-custom-site.ts`) or the advanced-site configuration module.
 */

const CRM_CUSTOM_MAX_BLOCKS = 30
const CRM_CUSTOM_SERVICE_MENU_LABELS = new Set(['registry', 'warranty', 'omb'])
const CRM_CUSTOM_HOME_MENU_ITEM: CrmCustomMenuItem = {
  id: 'system-menu-home',
  label: 'Home',
  kind: 'link',
  url: '/',
  newTab: false,
}

const CRM_CUSTOM_GRADIENT_THEMES: Record<string, { from: string; to: string; text: string }> = {
  'blue-orange': { from: '#2563eb', to: '#ea580c', text: '#1e3a8a' },
  'purple-pink': { from: '#7c3aed', to: '#db2777', text: '#581c87' },
  'cyan-blue': { from: '#0891b2', to: '#1e40af', text: '#0c4a6e' },
  'amber-red': { from: '#f59e0b', to: '#dc2626', text: '#b45309' },
  emerald: { from: '#10b981', to: '#047857', text: '#064e3b' },
  'indigo-violet': { from: '#4f46e5', to: '#7e22ce', text: '#312e81' },
  rose: { from: '#f43f5e', to: '#be185d', text: '#881337' },
  'teal-green': { from: '#0d9488', to: '#14532d', text: '#134e4a' },
  'sky-navy': { from: '#0ea5e9', to: '#1e3a8a', text: '#172554' },
  'slate-ink': { from: '#475569', to: '#1e293b', text: '#0f172a' },
  'lime-turquoise': { from: '#a3e635', to: '#14b8a6', text: '#3f6212' },
  'orange-magenta': { from: '#fb923c', to: '#db2777', text: '#9a3412' },
  'crimson-gold': { from: '#f43f5e', to: '#f59e0b', text: '#9f1239' },
  'violet-azure': { from: '#8b5cf6', to: '#0ea5e9', text: '#5b21b6' },
  'blush-lilac': { from: '#fbcfe8', to: '#c4b5fd', text: '#9d174d' },
  'sky-mint': { from: '#bae6fd', to: '#a7f3d0', text: '#0c4a6e' },
  'peach-cream': { from: '#fed7aa', to: '#fbcfe8', text: '#9a3412' },
  'olive-forest': { from: '#65a30d', to: '#166534', text: '#365314' },
  'sand-cocoa': { from: '#d6a354', to: '#7c4a1e', text: '#713f12' },
  'moss-slate': { from: '#4d7c0f', to: '#475569', text: '#365314' },
}

const getCrmCustomGradientTheme = (key: string) =>
  CRM_CUSTOM_GRADIENT_THEMES[String(key || '').trim()] || null

export const resolveCrmCustomGradientCss = (gradientKey: string, fallbackPrimary: string) => {
  const theme = getCrmCustomGradientTheme(gradientKey)
  if (!theme) {
    return {
      gradient: `linear-gradient(135deg, ${fallbackPrimary}, ${fallbackPrimary})`,
      primary: fallbackPrimary,
      wash: 'none',
    }
  }

  return {
    gradient: `linear-gradient(135deg, ${theme.from}, ${theme.to})`,
    primary: theme.text,
    wash: `linear-gradient(135deg, color-mix(in srgb, ${theme.from} 26%, white), color-mix(in srgb, ${theme.to} 34%, white))`,
  }
}

/** System workspace names must never become the public brand. */
const CRM_CUSTOM_SITE_DEFAULT_WORKSPACE_NAMES = new Set([
  '我的工作区',
  '三和万演示客户',
  '三和万测试客户',
])

export const isCrmCustomSiteDefaultWorkspaceName = (value: string): boolean =>
  CRM_CUSTOM_SITE_DEFAULT_WORKSPACE_NAMES.has(String(value || '').trim())

export const isCrmCustomServiceMenuItem = (
  item: Pick<CrmCustomMenuItem, 'kind' | 'label' | 'url'>,
) =>
  item.kind === 'service' ||
  (item.kind === 'link' &&
    !String(item.url || '').trim() &&
    CRM_CUSTOM_SERVICE_MENU_LABELS.has(String(item.label || '').trim().toLowerCase()))

export const getCrmCustomServiceMenuLabel = (servicePageType: unknown): string => {
  if (servicePageType === 'no_order_needed') return 'No Order Needed'
  if (servicePageType === 'warranty') return 'Warranty'
  if (servicePageType === 'omb') return 'OMB'
  if (servicePageType === 'registry') return 'Registry'
  return 'Customer Service'
}

export const withCrmCustomDefaultHomeMenuItem = (
  items: CrmCustomMenuItem[],
): CrmCustomMenuItem[] => {
  const hasHome = items.some(
    (item) => item.kind === 'link' && String(item.url || '').trim() === '/',
  )
  return hasHome ? items : [{ ...CRM_CUSTOM_HOME_MENU_ITEM }, ...items]
}

export const selectCrmCustomHomepageProducts = <T>(
  products: T[],
  randomValue = Math.random(),
): T[] => {
  const selected = [...products]
  if (selected.length <= 2) return selected

  const normalizedRandom = Number.isFinite(randomValue)
    ? Math.min(0.999999999, Math.max(0, randomValue))
    : 0
  let state = Math.floor(normalizedRandom * 0x100000000) >>> 0
  for (let index = selected.length - 1; index > 0; index -= 1) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0
    const swapIndex = state % (index + 1)
    ;[selected[index], selected[swapIndex]] = [selected[swapIndex], selected[index]]
  }
  return selected.slice(0, 3)
}

/** Keep singular compatibility routes out of canonical/structured-data URLs. */
export const resolveCrmCustomCanonicalPath = (pathname: unknown): string => {
  const rawPath = String(pathname || '/').trim() || '/'
  const path = rawPath.startsWith('/') ? rawPath : `/${rawPath}`
  if (path === '/product') return '/products'
  if (path.startsWith('/product/')) return `/products/${path.slice('/product/'.length)}`
  return path
}

const createDefaultMenu = (): CrmCustomMenu => ({
  enabled: true,
  sticky: true,
  transparentOnHero: false,
  items: [],
})

const createDefaultSettings = (
  brandName = '',
  contactEmail = '',
): CrmCustomSiteSettings => ({
  version: 1,
  status: 'published',
  theme: {
    categoryKey: 'other',
    accentColor: '#2563eb',
    gradientKey: '',
    headingFontKey: 'system',
    buttonStyle: 'rounded',
    logoUrl: '',
    logoText: brandName || 'Our Brand',
    faviconUrl: '',
    shareImageUrl: '',
    containerWidth: 1200,
    pageBackground: '#ffffff',
    animationEnabled: false,
  },
  homepageBlocks: [],
  headerMenu: {
    ...createDefaultMenu(),
    items: withCrmCustomDefaultHomeMenuItem([]),
  },
  footerMenu: createDefaultMenu(),
  footer: {
    enabled: true,
    slogan: 'We put your experience first, at every step.',
    contactEmail: contactEmail || 'support@yourwebsite.com',
    contactPhone: '',
    contactAddress: '',
    socials: [],
    copyright: `© ${new Date().getFullYear()} ${brandName || 'Our Brand'}. All rights reserved.`,
    showPolicyLinks: true,
  },
})

const normalizeCrmCustomMenu = (value: unknown, includeDefaultHome = false): CrmCustomMenu => {
  const defaults = createDefaultMenu()
  const raw = value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Partial<CrmCustomMenu>)
    : {}

  return {
    enabled: raw.enabled !== false,
    sticky: raw.sticky === true,
    transparentOnHero: raw.transparentOnHero === true,
    items: Array.isArray(raw.items)
      ? raw.items
          .slice(0, 20)
          .map((item, index) => normalizeCrmCustomMenuItem(item, index))
          .filter((item): item is CrmCustomMenuItem => Boolean(item))
      : includeDefaultHome
        ? withCrmCustomDefaultHomeMenuItem(defaults.items)
        : defaults.items,
  }
}

const normalizeCrmCustomMenuItem = (value: unknown, index: number): CrmCustomMenuItem | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as Partial<CrmCustomMenuItem>
  const supportedKind =
    raw.kind === 'page' || raw.kind === 'product' || raw.kind === 'catalog' || raw.kind === 'service'
      ? raw.kind
      : 'link'
  const legacyService =
    supportedKind === 'link' &&
    !String(raw.url || '').trim() &&
    CRM_CUSTOM_SERVICE_MENU_LABELS.has(String(raw.label || '').trim().toLowerCase())
  const kind = legacyService ? 'service' : supportedKind
  const children = Array.isArray(raw.children)
    ? raw.children
        .slice(0, 8)
        .map((item, childIndex) => normalizeCrmCustomMenuItem(item, childIndex))
        .filter((item): item is CrmCustomMenuItem => Boolean(item))
    : undefined

  return {
    id: String(raw.id || `menu-${index}`),
    label: String(raw.label || '').slice(0, 80),
    kind,
    pageId: kind === 'page' ? String(raw.pageId || '') : undefined,
    productSlug: kind === 'product' ? String(raw.productSlug || '') : undefined,
    url: kind === 'link' ? String(raw.url || '') : undefined,
    newTab: kind === 'link' && raw.newTab === true,
    children: children?.length ? children : undefined,
  }
}

/** Normalize only the data used by the public renderer. The API already sanitizes it. */
export const normalizeCrmCustomSiteSettings = (
  value: unknown,
  fallback: { brandName?: string; contactEmail?: string } = {},
): CrmCustomSiteSettings => {
  const raw = value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Partial<CrmCustomSiteSettings>)
    : {}
  const defaults = createDefaultSettings(fallback.brandName || '', fallback.contactEmail || '')
  const theme = (raw.theme || {}) as Partial<CrmCustomSiteTheme>
  const footer = (raw.footer || {}) as Partial<CrmCustomSiteFooter>

  return {
    version: 1,
    status: raw.status === 'draft' ? 'draft' : 'published',
    theme: {
      categoryKey:
        theme.categoryKey === 'home' ||
        theme.categoryKey === 'beauty' ||
        theme.categoryKey === 'electronics' ||
        theme.categoryKey === 'outdoor' ||
        theme.categoryKey === 'baby' ||
        theme.categoryKey === 'health' ||
        theme.categoryKey === 'other'
          ? theme.categoryKey
          : undefined,
      visualPresetKey:
        theme.visualPresetKey === 'ai-grand-v2' ? 'ai-grand-v2' : undefined,
      accentColor:
        typeof theme.accentColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(theme.accentColor)
          ? theme.accentColor
          : defaults.theme.accentColor,
      gradientKey:
        typeof theme.gradientKey === 'string' && getCrmCustomGradientTheme(theme.gradientKey)
          ? theme.gradientKey
          : defaults.theme.gradientKey,
      headingFontKey:
        typeof theme.headingFontKey === 'string' && theme.headingFontKey
          ? theme.headingFontKey
          : defaults.theme.headingFontKey,
      bodyFontKey:
        typeof theme.bodyFontKey === 'string' && theme.bodyFontKey
          ? theme.bodyFontKey
          : undefined,
      buttonStyle:
        theme.buttonStyle === 'pill' || theme.buttonStyle === 'square'
          ? theme.buttonStyle
          : 'rounded',
      logoUrl: typeof theme.logoUrl === 'string' ? theme.logoUrl : '',
      logoText:
        typeof theme.logoText === 'string' && theme.logoText
          ? theme.logoText
          : defaults.theme.logoText,
      faviconUrl: typeof theme.faviconUrl === 'string' ? theme.faviconUrl : '',
      shareImageUrl: typeof theme.shareImageUrl === 'string' ? theme.shareImageUrl : '',
      containerWidth:
        typeof theme.containerWidth === 'number' && theme.containerWidth >= 900 && theme.containerWidth <= 1600
          ? Math.round(theme.containerWidth)
          : defaults.theme.containerWidth,
      pageBackground:
        typeof theme.pageBackground === 'string' && /^#[0-9a-fA-F]{6}$/.test(theme.pageBackground)
          ? theme.pageBackground
          : defaults.theme.pageBackground,
      animationEnabled: theme.animationEnabled === true,
    },
    homepageBlocks: Array.isArray(raw.homepageBlocks)
      ? (raw.homepageBlocks as CrmCustomBlock[]).slice(0, CRM_CUSTOM_MAX_BLOCKS)
      : defaults.homepageBlocks,
    headerMenu: normalizeCrmCustomMenu(raw.headerMenu, true),
    footerMenu: normalizeCrmCustomMenu(raw.footerMenu),
    footer: {
      enabled: footer.enabled !== false,
      slogan: typeof footer.slogan === 'string' ? footer.slogan : defaults.footer.slogan,
      contactEmail:
        typeof footer.contactEmail === 'string' && footer.contactEmail
          ? footer.contactEmail
          : defaults.footer.contactEmail,
      contactPhone: typeof footer.contactPhone === 'string' ? footer.contactPhone : '',
      contactAddress: typeof footer.contactAddress === 'string' ? footer.contactAddress : '',
      socials: Array.isArray(footer.socials)
        ? footer.socials.filter(
            (item) => typeof item?.key === 'string' && typeof item?.url === 'string',
          )
        : [],
      copyright:
        typeof footer.copyright === 'string' && footer.copyright
          ? footer.copyright
          : defaults.footer.copyright,
      showPolicyLinks: footer.showPolicyLinks !== false,
    },
    contactNotify: (() => {
      const notify = (raw.contactNotify || {}) as Partial<CrmCustomSiteContactNotify>
      return {
        enabled: notify.enabled !== false,
        notifyEmail: typeof notify.notifyEmail === 'string' ? notify.notifyEmail : '',
        autoReply: notify.autoReply !== false,
      }
    })(),
  }
}

export const resolveCrmCustomMenuItemUrl = (
  item: CrmCustomMenuItem,
  pages: CrmCustomSitePage[],
  products: CrmCustomSiteProduct[],
  servicePagePath = '/registry',
): { url: string; external: boolean } => {
  if (isCrmCustomServiceMenuItem(item)) {
    const path = String(servicePagePath || '/registry').trim()
    return { url: path.startsWith('/') ? path : `/${path}`, external: false }
  }
  if (item.kind === 'page') {
    const targetSlug = inferCrmCustomPageSlug(item.label, item.id)
    const page = pages.find(
      (candidate) =>
        candidate.id === item.pageId ||
        (targetSlug && candidate.slug === targetSlug),
    )
    return page ? { url: `/${page.slug}`, external: false } : { url: '#', external: false }
  }
  if (item.kind === 'product') {
    const slug = String(item.productSlug || '').trim()
    const exists = products.some((candidate) => candidate.slug === slug)
    return { url: exists ? `/products/${slug}` : '#', external: false }
  }
  if (item.kind === 'catalog') return { url: '/products', external: false }
  const url = String(item.url || '#').trim()
  return { url, external: !url.startsWith('/') }
}

const CRM_CUSTOM_PAGE_LABEL_SLUGS: Record<string, string> = {
  about: 'about',
  aboutus: 'about',
  contact: 'contact',
  contactus: 'contact',
  privacy: 'privacy',
  privacypolicy: 'privacy',
  terms: 'terms',
  termsofuse: 'terms',
  termsandconditions: 'terms',
  vip: 'vip-club',
  vipclub: 'vip-club',
}

export const inferCrmCustomPageSlug = (label: unknown, id: unknown = ''): string => {
  const normalizedLabel = String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
  const fromLabel = CRM_CUSTOM_PAGE_LABEL_SLUGS[normalizedLabel]
  if (fromLabel) return fromLabel

  const normalizedId = String(id || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
  for (const slug of ['about', 'contact', 'privacy', 'terms', 'vip-club']) {
    if (normalizedId.includes(slug.replace('-', ''))) return slug
  }
  return ''
}

export const resolveCrmCustomBlockCtaUrl = (
  settings: Record<string, unknown>,
  pages: CrmCustomSitePage[],
  products: CrmCustomSiteProduct[],
): { url: string; external: boolean } | null => {
  const kind = String(settings.ctaKind || 'none')
  if (kind === 'page') {
    const targetId = String(settings.ctaPageId || '')
    const targetSlug =
      String(settings.ctaPageSlug || '').trim().toLowerCase() ||
      inferCrmCustomPageSlug(settings.ctaLabel)
    const page = pages.find(
      (candidate) => candidate.id === targetId || (targetSlug && candidate.slug === targetSlug),
    )
    return page ? { url: `/${page.slug}`, external: false } : null
  }
  if (kind === 'product') {
    const product = products.find(
      (candidate) => candidate.slug === String(settings.ctaProductSlug || ''),
    )
    return product ? { url: `/products/${product.slug}`, external: false } : null
  }
  if (kind === 'link') {
    const url = String(settings.ctaUrl || '').trim()
    return url ? { url, external: true } : null
  }
  return null
}

export interface CrmCustomSitePublicContent {
  site: CrmPublicSite | null
  settings: CrmCustomSiteSettings | null
  pages: CrmCustomSitePage[]
  products: CrmCustomSiteProduct[]
  redirects?: Array<{ from: string; to: string }>
}

// Route transitions can reuse the last payload immediately, but every loader still
// revalidates over the network so editor changes are never hidden by this cache.
const publicContentBySiteId = new Map<string, CrmCustomSitePublicContent>()
const publicContentByHost = new Map<string, CrmCustomSitePublicContent>()

const normalizePublicContentHost = (value: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[/#?].*$/, '')

export const getCachedCrmCustomSitePublicContent = ({
  host = '',
  siteId = '',
}: { host?: string; siteId?: string } = {}): CrmCustomSitePublicContent | null => {
  const cleanSiteId = String(siteId || '').trim()
  if (cleanSiteId && publicContentBySiteId.has(cleanSiteId)) {
    return publicContentBySiteId.get(cleanSiteId) || null
  }
  const cleanHost = normalizePublicContentHost(host)
  return cleanHost ? publicContentByHost.get(cleanHost) || null : null
}

export const cacheCrmCustomSitePublicContent = (
  content: CrmCustomSitePublicContent,
  host = '',
  siteId = '',
) => {
  const resolvedSiteId = String(content.site?.id || siteId || '').trim()
  if (resolvedSiteId) publicContentBySiteId.set(resolvedSiteId, content)
  const cleanHost = normalizePublicContentHost(host)
  if (cleanHost) publicContentByHost.set(cleanHost, content)
}

// ---------------------------------------------------------------------------
// 站点事件埋点（PV/UV、产品浏览、购买点击、表单提交）
// ---------------------------------------------------------------------------

const CRM_SITE_EVENT_VISITOR_KEY = 'crm-site-visitor-id'

export const getCrmSiteVisitorId = (): string => {
  if (typeof window === 'undefined') return ''
  try {
    const existing = window.localStorage.getItem(CRM_SITE_EVENT_VISITOR_KEY)
    if (existing) return existing
    const created =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
    window.localStorage.setItem(CRM_SITE_EVENT_VISITOR_KEY, created)
    return created
  } catch {
    return ''
  }
}

export const trackCrmSiteEvent = ({
  siteId,
  eventName,
  path = '',
  payload = {},
}: {
  siteId: string
  eventName: string
  path?: string
  payload?: Record<string, unknown>
}) => {
  if (typeof window === 'undefined' || !siteId) return
  try {
    void fetch('/api/crm/public-site-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        siteId,
        visitorId: getCrmSiteVisitorId(),
        eventName,
        path: path || window.location.pathname,
        payload,
      }),
    }).catch(() => {})
  } catch {
    // 埋点失败不影响前台
  }
}

export const fetchCrmCustomSitePublicContent = async ({
  host = '',
  siteId = '',
  previewToken = '',
}: {
  host?: string
  siteId?: string
  previewToken?: string
} = {}): Promise<CrmCustomSitePublicContent> => {
  const query = new URLSearchParams()
  if (siteId) query.set('siteId', siteId)
  if (host && !siteId) query.set('host', host)
  if (previewToken) query.set('previewToken', previewToken)

  const response = await fetch(
    `/api/crm/public-custom-site${query.toString() ? `?${query.toString()}` : ''}`,
    {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    },
  )
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { message?: string } | null
    throw new Error(payload?.message || 'Failed to load custom website content.')
  }

  const payload = (await response.json()) as {
    site: CrmPublicSite | null
    settings: unknown
    pages: unknown[]
    products: unknown[]
    redirects?: Array<{ from: string; to: string }>
  }
  const content: CrmCustomSitePublicContent = {
    site: payload.site || null,
    settings: payload.settings ? normalizeCrmCustomSiteSettings(payload.settings) : null,
    pages: Array.isArray(payload.pages) ? (payload.pages as CrmCustomSitePage[]) : [],
    products: Array.isArray(payload.products) ? (payload.products as CrmCustomSiteProduct[]) : [],
    redirects: Array.isArray(payload.redirects) ? payload.redirects : [],
  }
  if (!previewToken) cacheCrmCustomSitePublicContent(content, host, siteId)
  return content
}
